// Erscheinungsbild einer ZeigBanner-Instanz (White-Label). Server und Oberfläche nutzen
// dieselbe Ableitung, damit die Live-Vorschau im Admin-Bereich exakt dem Ergebnis entspricht.
import type { PlatformId } from './types.js';

export type CornerStyle = 'square' | 'soft' | 'round';
export type FontChoice = 'dm-sans' | 'system' | 'custom';
export type BrandAssetSlot = 'logo' | 'logoDark' | 'favicon' | 'fontRegular' | 'fontBold';

export interface BrandingSettings {
  appName: string;
  /** Optionaler Zusatz neben dem Logo in der Kopfzeile, z. B. „Banner-Review“. */
  headerLabel: string;
  accent: string;
  ink: string;
  success: string;
  corners: CornerStyle;
  font: FontChoice;
  fontName: string;
  loginHeadline: string;
  loginText: string;
  providerName: string;
  supportEmail: string;
  imprintUrl: string;
  privacyUrl: string;
  showPoweredBy: boolean;
  customCss: string;
  /** Hochgeladene Dateien: Ablageschlüssel je Platz. */
  assets: Partial<Record<BrandAssetSlot, string>>;
}

export interface GeneralSettings {
  /** Vorauswahl der Zielplattformen für neue Kampagnen. */
  defaultPlatforms: PlatformId[];
  /** Zusätzliche Agentur-Domains für die Microsoft-/OIDC-Anmeldung. */
  agencyDomains: string[];
}

export const DEFAULT_BRANDING: BrandingSettings = {
  appName: 'ZeigBanner',
  headerLabel: '',
  accent: '#3355ff',
  ink: '#16181d',
  success: '#12a46f',
  corners: 'soft',
  font: 'dm-sans',
  fontName: '',
  // Text zwischen *Sternchen* erscheint in der Akzentfarbe.
  loginHeadline: 'Freigaben ohne *Mail-Pingpong.*',
  loginText:
    'HTML5-Banner, GIFs und Videos so zeigen, wie sie später laufen. Feedback direkt am Motiv, Freigabe mit Protokoll.',
  providerName: '',
  supportEmail: '',
  imprintUrl: '',
  privacyUrl: '',
  showPoweredBy: true,
  customCss: '',
  assets: {},
};

export const DEFAULT_GENERAL: GeneralSettings = {
  defaultPlatforms: ['google_ads', 'cm360'],
  agencyDomains: [],
};

/** Was jede Seite (auch ohne Anmeldung) über das Erscheinungsbild wissen muss. */
export interface PublicBranding {
  appName: string;
  headerLabel: string;
  logoUrl: string;
  logoDarkUrl: string | null;
  faviconUrl: string;
  loginHeadline: string;
  loginText: string;
  providerName: string;
  supportEmail: string;
  imprintUrl: string;
  privacyUrl: string;
  showPoweredBy: boolean;
  version: string;
}

export const assetUrl = (key: string) => `/branding/${key}`;

// ---------- Farben ----------

type Rgb = [number, number, number];

export function parseHex(hex: string): Rgb | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const toHex = ([r, g, b]: Rgb) =>
  `#${[r, g, b]
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/** Mischt `a` mit `b`; `amount` = Anteil von b (0…1). */
export function mix(a: string, b: string, amount: number): string {
  const ca = parseHex(a) ?? [0, 0, 0];
  const cb = parseHex(b) ?? [255, 255, 255];
  return toHex([0, 1, 2].map((i) => ca[i] + (cb[i] - ca[i]) * amount) as Rgb);
}

export function rgba(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex) ?? [0, 0, 0];
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Relative Leuchtdichte nach WCAG. */
export function luminance(hex: string): number {
  const c = (parseHex(hex) ?? [0, 0, 0]).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

const CORNERS: Record<CornerStyle, { radius: string; sm: string; lg: string; chip: string; rule: string }> = {
  square: { radius: '0px', sm: '0px', lg: '0px', chip: '0px', rule: '6px' },
  soft: { radius: '8px', sm: '6px', lg: '12px', chip: '999px', rule: '4px' },
  round: { radius: '14px', sm: '10px', lg: '20px', chip: '999px', rule: '3px' },
};

const FONT_STACK =
  "'DM Sans Variable', 'DM Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
const SYSTEM_STACK =
  "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";
export const CUSTOM_FONT_FAMILY = 'ZeigBanner Brand';

/** Leitet aus drei Grundfarben, Ecken und Schrift alle CSS-Variablen der Oberfläche ab. */
export function brandTokens(
  b: Pick<BrandingSettings, 'accent' | 'ink' | 'success' | 'corners' | 'font'>,
): Record<string, string> {
  const ink = parseHex(b.ink) ? b.ink : DEFAULT_BRANDING.ink;
  const accent = parseHex(b.accent) ? b.accent : DEFAULT_BRANDING.accent;
  const success = parseHex(b.success) ? b.success : DEFAULT_BRANDING.success;
  const white = '#ffffff';
  const lightSuccess = luminance(success) > 0.45;
  const corners = CORNERS[b.corners] ?? CORNERS.soft;
  const font =
    b.font === 'system'
      ? SYSTEM_STACK
      : b.font === 'custom'
        ? `'${CUSTOM_FONT_FAMILY}', ${FONT_STACK}`
        : FONT_STACK;
  return {
    '--font-sans': font,
    '--ink': ink,
    '--ink-2': mix(ink, white, 0.18),
    '--muted': mix(ink, white, 0.45),
    '--line': mix(ink, white, 0.88),
    '--line-strong': ink,
    '--surface-2': mix(ink, white, 0.955),
    '--surface-3': mix(ink, white, 0.915),
    '--overlay': rgba(ink, 0.58),
    ...accentTokens(accent, ink),
    // Helle Signalfarben (z. B. Mint) werden zur Fläche mit dunkler Schrift,
    // kräftige zur Schrift auf hellem Grund.
    '--ok': lightSuccess ? mix(success, '#000000', 0.38) : success,
    '--ok-bg': lightSuccess ? success : mix(success, white, 0.84),
    '--ok-ink': lightSuccess ? ink : mix(success, '#000000', 0.45),
    '--radius': corners.radius,
    '--radius-sm': corners.sm,
    '--radius-lg': corners.lg,
    '--radius-chip': corners.chip,
    '--rule': corners.rule,
  };
}

/**
 * Akzent-Variablen aus einer Farbe – für die Instanz und für Freigabe-Seiten in der Farbe des Kunden.
 * Für Text auf Weiß wird die Farbe so weit abgedunkelt, bis der Kontrast reicht (WCAG AA, 4,5:1).
 */
export function accentTokens(accentHex: string, inkHex: string): Record<string, string> {
  const accent = parseHex(accentHex) ? accentHex : DEFAULT_BRANDING.accent;
  const ink = parseHex(inkHex) ? inkHex : DEFAULT_BRANDING.ink;
  const white = '#ffffff';
  let text = accent;
  for (let i = 1; i <= 12 && contrast(text, white) < 4.5; i += 1) text = mix(accent, '#000000', i * 0.07);
  return {
    '--accent': accent,
    '--accent-hover': mix(accent, '#000000', 0.12),
    '--accent-ink': contrast(accent, white) >= 3 ? white : ink,
    '--accent-soft': rgba(accent, 0.14),
    '--accent-text': text,
  };
}

export function brandCss(b: BrandingSettings): string {
  const vars = Object.entries(brandTokens(b))
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  const faces: string[] = [];
  const format = (key: string) =>
    ({ woff2: 'woff2', woff: 'woff', ttf: 'truetype', otf: 'opentype' })[key.split('.').pop() ?? ''] ??
    'woff2';
  if (b.font === 'custom' && b.assets.fontRegular) {
    faces.push(
      `@font-face {\n  font-family: '${CUSTOM_FONT_FAMILY}';\n  src: url('${assetUrl(b.assets.fontRegular)}') format('${format(b.assets.fontRegular)}');\n  font-weight: 400;\n  font-display: swap;\n}`,
    );
  }
  if (b.font === 'custom' && b.assets.fontBold) {
    faces.push(
      `@font-face {\n  font-family: '${CUSTOM_FONT_FAMILY}';\n  src: url('${assetUrl(b.assets.fontBold)}') format('${format(b.assets.fontBold)}');\n  font-weight: 600 900;\n  font-display: swap;\n}`,
    );
  }
  return [
    '/* Erzeugt aus den Einstellungen unter „Einstellungen → Erscheinungsbild“. */',
    ...faces,
    `:root {\n${vars}\n}`,
    b.customCss ? `/* Eigenes CSS */\n${b.customCss}` : '',
  ]
    .filter(Boolean)
    .join('\n\n');
}
